import { useQuery } from 'react-query';
import { QueryId, STALE_TIME } from '@/integrations/content/utils';
import formatUrlSearchParameters from '@/libraries/formatters/format-url-search-parameters';
import getCulture from '@/libraries/getters/get-culture';
import getLocale from '@/libraries/getters/get-locale';

type UseQuerySearchAvailableDatesData =
  TravelPortal.Responses.SearchAvailableDates;

interface UseQuerySearchAvailableDatesOptions {
  initialData?: UseQuerySearchAvailableDatesData;
}

interface UseQuerySearchAvailableDatesParameters {
  adultsNumber: string;
  area?: string;
  childrenAges: string;
  countryIds?: string;
  departureIds: string;
  destinationIds?: string;
}

const useQuerySearchAvailableDates = (
  parameters: UseQuerySearchAvailableDatesParameters,
  options?: UseQuerySearchAvailableDatesOptions,
) => {
  const locale = getLocale();

  const query = useQuery<UseQuerySearchAvailableDatesData>(
    [QueryId.SEARCH_AVAILABLE_DATES, parameters, locale],
    async () => {
      const baseUrl = '/api/search/available-dates';
      const searchParameters = formatUrlSearchParameters(parameters);

      const fetchResponse = await fetch(
        `${baseUrl}?${searchParameters.toString()}`,
        {
          headers: {
            ['Accept-Language']: getCulture(locale),
            ['Content-Type']: 'application/json',
          },
          method: 'GET',
        },
      )
        .then(response => {
          if (response.ok) return response.json();

          throw new Error(`${response.status} - ${response.statusText}`);
        })
        .catch(error => {
          console.error('hooks.useQuerySearchAvailableDates', error);

          throw error;
        });

      return fetchResponse;
    },
    {
      enabled: !!parameters.area && !!parameters.adultsNumber,
      initialData: options?.initialData,
      keepPreviousData: true,
      staleTime: STALE_TIME,
    },
  );

  return query;
};

export default useQuerySearchAvailableDates;
